<template>
  <div id="vessel-movement-type-list" class="h-full">
    <div class="flex flex-wrap justify-between items-center">
      <div class="flex w-1/3 justify-start my-4">
        <div class="aisea-text-18-600-dark-gray">船舶動静種別</div>
      </div>
      <div class="flex w-2/3 justify-end items-center">
        <vs-button
          icon="add"
          :color="AISEA_COLOR.BLUE"
          type="border"
          style="margin: 0 0.6rem; height: 2.7rem;"
          :disabled="addButtonDisabled"
          @click="
            () => {
              setActionDialogAdd();
              showActionDialog = true;
            }
          "
          ><span class="aisea-text-14-400-blue">新規登録</span></vs-button
        >
      </div>
    </div>

    <vs-divider :color="AISEA_COLOR.LIGHT_GRAY" class="my-0" />

    <ag-grid-vue
      :components="components"
      :gridOptions="gridOptions"
      class="ag-theme-material w-full ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="tableData"
      domLayout="autoHeight"
      rowSelection="multiple"
      colResizeDefault="shift"
      :rowDragManaged="true"
      :animateRows="true"
      :floatingFilter="false"
      :suppressMovableColumns="true"
      :suppressPaginationPanel="true"
      :suppressDragLeaveHidesColumns="true"
      :suppressCellFocus="true"
      :suppressRowClickSelection="true"
      :suppressNoRowsOverlay="false"
      @gridReady="onGridReady"
      @rowDragEnter="onRowDragEnter"
      @rowDragEnd="onRowDragEnd"
    />

    <vs-prompt
      title=""
      :color="AISEA_COLOR.BLUE"
      buttons-hidden
      :active.sync="showActionDialog"
      class="vessel-movement-type-list-add-dialog"
    >
      <!-- 独自ヘッダー -->
      <div class="vessel-movement-type-list-dialog-header">
        <span class="vessel-movement-type-list-dialog-header-title">{{
          actionDialogTitle
        }}</span>
        <div
          class="vessel-movement-type-list-dialog-header-close"
          role="button"
          tabindex="0"
          @click="onActionDialogCancel"
          @keydown.enter.prevent="onActionDialogCancel"
          @keydown.space.prevent="onActionDialogCancel"
        >
          <vs-icon icon="close" size="22px" />
        </div>
      </div>

      <div class="required-info-edit-form-container">
        <div class="form-description">
          必要事項を入力してください。「<span class="required-mark">*</span
          >」は入力必須項目です。
        </div>

        <div class="form-item">
          <div class="form-item-title">
            船舶動静種別<span class="required-mark">*</span>
          </div>
          <div class="input-area">
            <vs-input
              class="w-full"
              v-model="dialogName"
              :color="AISEA_COLOR.BLUE"
              placeholder="船舶動静種別を入力してください"
              :disabled="dialogTargetRowData && dialogTargetRowData.is_default"
            />
          </div>
        </div>

        <div class="form-item">
          <div class="form-item-title">
            動静種別カラー<span class="required-mark">*</span>
          </div>
          <div class="input-area color-select-wrapper" ref="colorSelectWrapper">
            <div
              ref="colorTrigger"
              class="color-select-trigger"
              :class="{
                placeholder: !dialogSelectedColor,
                open: colorDropdownOpen,
              }"
              @click="toggleColorDropdown"
            >
              <span v-if="dialogSelectedColor" class="color-select-value">
                <color-chip
                  class="color-select-trigger-chip"
                  :color="dialogSelectedColor"
                  :focused="false"
                />
              </span>
              <span v-else class="color-select-placeholder">
                カラーを選択してください
              </span>
              <feather-icon
                class="color-select-chevron"
                icon="ChevronDownIcon"
                svgClasses="h-4 w-4"
              />
            </div>
            <transition name="color-dropdown-fade">
              <div
                v-show="colorDropdownOpen"
                ref="colorMenu"
                class="color-select-menu color-select-menu-fixed"
                :style="colorMenuStyle"
                @click.stop
              >
                <div class="color-select-menu-label">
                  カラーを選択してください
                </div>
                <div class="color-select-swatch-grid">
                  <div
                    v-for="color in colorOptions"
                    :key="color"
                    class="color-select-swatch-wrap"
                    :class="{ selected: dialogSelectedColor === color }"
                    :style="
                      dialogSelectedColor === color
                        ? { outline: `2px solid ${color}` }
                        : {}
                    "
                    @click="onSelectColor(color)"
                  >
                    <color-chip
                      class="color-select-swatch"
                      :color="color"
                      :focused="false"
                    />
                  </div>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <div class="form-item">
          <div class="form-item-title">
            デフォルトの時間単位<span class="required-mark">*</span>
          </div>
          <div class="input-area">
            <dropdown-select
              class="filter-input"
              :selectOptions="defaultTimeUnitOptions"
              :selectedValue.sync="dialogDefaultMinutes"
              placeholder="作業時間を選択してください"
              :border="true"
            ></dropdown-select>
          </div>
        </div>

        <div class="form-item-radio">
          <div class="form-item-radio-title">
            選択肢への表示有無<span class="required-mark">*</span>
          </div>
          <div class="input-area py-[10px] px-[4px]">
            <vs-radio
              class="mr-5"
              v-model="dialogShowOnCreating"
              :vs-value="true"
              vs-name="showOnCreating"
              :color="AISEA_COLOR.BLUE"
            >
              表示する
            </vs-radio>
            <vs-radio
              v-model="dialogShowOnCreating"
              :vs-value="false"
              vs-name="showOnCreating"
              :color="AISEA_COLOR.BLUE"
            >
              表示しない
            </vs-radio>
          </div>
          <span class="form-item-radio-description"
            >「表示する」を選択した場合、打刻時の作業種別選択肢に表示されます。「表示しない」を選択した場合、選択肢として表示されません。</span
          >
        </div>
        <span class="validation-label">{{ getDialogValidationMessage }}</span>

        <div class="dialog-footer-button-container">
          <div class="dialog-footer-button">
            <vs-button
              type="filled"
              @click="onActionDialogCancel"
              :color="AISEA_COLOR.WHITE"
              text-color="#000000"
              >キャンセル
            </vs-button>
            <vs-button
              :color="AISEA_COLOR.BLUE"
              :disabled="getDialogValidationMessage != null"
              @click="
                () => {
                  if (actionMode == 'edit') {
                    onEdit();
                  } else if (actionMode == 'add') {
                    onAdd();
                  }
                }
              "
              >保存
            </vs-button>
          </div>
        </div>
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import CellRendererColorHex from "./cell-renderer/CellRendererColorHex.vue";
import CellRendererRowActionButtons from "./cell-renderer/CellRendererRowActionButtons.vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import DropdownSelect from "../components/DropdownSelect.vue";
import ColorChip from "../components/ColorChip.vue";
import { TASK_KIND_COLOR_PALETTE } from "@/config/const.js";

const enterpriseManningVesselStatusApi = AiseaApiFactory.get(
  "enterpriseManningVesselStatus"
);

export default {
  components: {
    AgGridVue,
    CellRendererColorHex,
    CellRendererRowActionButtons,
    DropdownSelect,
    ColorChip,
  },
  mixins: [DeviceInfoMixin, ColorMixin],
  props: {
    activeSidebarCrew: Boolean,
    activeTab: Number,
  },
  data() {
    return {
      colorOptions: TASK_KIND_COLOR_PALETTE.map(element => element.normal),

      defaultTimeUnitOptions: [
        { text: "15分", value: 15 },
        { text: "30分", value: 30 },
        { text: "60分", value: 60 },
        { text: "120分", value: 120 },
        { text: "180分", value: 180 },
        { text: "240分", value: 240 },
        { text: "480分", value: 480 },
        { text: "720分", value: 720 },
        { text: "960分", value: 960 },
      ],

      dialogName: "",
      dialogSelectedColor: null,
      dialogDefaultMinutes: null,
      dialogShowOnCreating: true,
      dialogTargetRowData: null,

      tableData: [],

      showActionDialog: false,

      colorDropdownOpen: false,
      colorMenuStyle: {},

      actionMode: null,
      actionDialogTitle: "",

      updatedIdList: [],

      orderIdsBeforeDrag: null,

      gridApi: null,

      gridOptions: {
        // 行データのIDを指定
        getRowId: params => String(params.data.id),
        localeText: {
          noRowsToShow:
            '<div id="no-rows-vessel-movement-type-list" class="aisea-text-16-400-black">表示する船舶動静種別がありません</div>',
        },
        context: {
          componentParent: this,
        },
      },
      defaultColDef: {
        resizable: true,
        suppressMenu: true,
      },
      columnDefs: [],
      components: {
        CellRendererColorHex,
        CellRendererRowActionButtons,
      },
    };
  },
  created() {
    this.initializeColumnDef();
    this.fetchTableData();
  },
  mounted() {
    const routerViewElement = document.getElementsByClassName("router-view");
    if (routerViewElement[0]) {
      routerViewElement[0].style.padding = "2.2rem";
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClickForColorDropdown);
  },
  watch: {
    colorDropdownOpen(val) {
      if (val) {
        this.$nextTick(() => {
          document.addEventListener(
            "click",
            this.onDocumentClickForColorDropdown
          );
        });
      } else {
        document.removeEventListener(
          "click",
          this.onDocumentClickForColorDropdown
        );
      }
    },
  },
  computed: {
    addButtonDisabled() {
      return this.tableData.length >= 50;
    },
    getDialogValidationMessage() {
      if (!this.dialogName) {
        return "入力されていない項目があります";
      }
      if (this.dialogName.length > 255) {
        return "船舶動静種別は255文字以下で入力してください";
      }
      if (
        this.dialogDefaultMinutes === null ||
        this.dialogDefaultMinutes === undefined
      ) {
        return "入力されていない項目があります";
      }
      if (!this.dialogSelectedColor) {
        return "入力されていない項目があります";
      }
      return null;
    },
  },
  methods: {
    toggleColorDropdown() {
      if (this.colorDropdownOpen) {
        this.colorDropdownOpen = false;
        return;
      }
      // 初回でも重ならないよう、位置を計算してからメニューを表示する
      this.$nextTick(() => {
        this.updateColorMenuPosition();
        this.$nextTick(() => {
          this.colorDropdownOpen = true;
        });
      });
    },
    updateColorMenuPosition() {
      const trigger = this.$refs.colorTrigger;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      this.colorMenuStyle = {
        position: "fixed",
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        minWidth: `${rect.width}px`,
        zIndex: 10000,
      };
    },
    onSelectColor(color) {
      this.dialogSelectedColor = color;
      this.colorDropdownOpen = false;
    },
    onDocumentClickForColorDropdown(e) {
      if (
        !this.colorDropdownOpen ||
        !this.$refs.colorSelectWrapper ||
        this.$refs.colorSelectWrapper.contains(e.target)
      ) {
        return;
      }
      this.colorDropdownOpen = false;
    },
    initializeColumnDef() {
      const self = this;
      this.columnDefs = [
        {
          headerName: "No.",
          field: "sort_order",
          rowDrag: true,
          minWidth: 85,
          maxWidth: 85,
          cellStyle: params => self.customizeCellStyle(params),
        },
        {
          headerName: "船舶動静種別",
          field: "name",
          minWidth: 200,
          flex: 1,
          cellStyle: params => self.customizeCellStyle(params),
        },
        {
          headerName: "デフォルトの\n時間単位",
          field: "default_minutes",
          minWidth: 100,
          flex: 1,
          valueGetter: params => {
            if (!params.data) return "";
            const opt = self.defaultTimeUnitOptions.find(
              o => o.value === params.data.default_minutes
            );
            return opt ? opt.text : `${params.data.default_minutes}分`;
          },
          cellStyle: params => self.customizeCellStyle(params),
        },
        {
          headerName: "種別カラー",
          field: "color_hex",
          minWidth: 120,
          flex: 1,
          cellRenderer: "CellRendererColorHex",
          cellStyle: params => self.customizeCellStyle(params),
        },
        {
          headerName: "選択肢への表示有無",
          field: "show_on_creating",
          minWidth: 170,
          flex: 1,
          valueGetter: params => {
            return params.data.show_on_creating ? "表示する" : "表示しない";
          },
          cellStyle: params => self.customizeCellStyle(params),
        },
        {
          headerName: "操作",
          field: "action",
          width: 72,
          cellRendererFramework: "CellRendererRowActionButtons",
          pinned: "right",
          resizable: false,
          cellRendererParams: {
            edit: data => {
              this.setActionDialogEdit(data);
              this.showActionDialog = true;
            },
          },
          headerClass: "ag-header-cell-center",
          cellStyle: params => {
            const baseStyle = self.customizeCellStyle(params) || {};
            return { ...baseStyle, textAlign: "center" };
          },
        },
      ];
    },
    customizeCellStyle(params) {
      if (params.data && this.updatedIdList.includes(params.data.id)) {
        return {
          backgroundColor: "#FFF8E3",
        };
      }
      return null;
    },
    setActionDialogEdit(rowData) {
      this.dialogTargetRowData = rowData;
      this.dialogSelectedColor = rowData.color_hex
        ? `#${rowData.color_hex}`
        : null;
      this.dialogName = rowData.name;
      this.dialogDefaultMinutes = rowData.default_minutes;
      this.dialogShowOnCreating = rowData.show_on_creating;
      this.actionMode = "edit";
      this.actionDialogTitle = "船舶動静種別管理（編集）";
      this.colorDropdownOpen = false;
    },
    setActionDialogAdd() {
      this.clearDialogForm();
      this.actionMode = "add";
      this.actionDialogTitle = "船舶動静種別管理（新規）";
    },
    clearDialogForm() {
      this.dialogName = "";
      this.dialogSelectedColor = null;
      this.dialogDefaultMinutes = 60;
      this.dialogShowOnCreating = true;
      this.dialogTargetRowData = null;
      this.colorDropdownOpen = false;
    },
    onActionDialogCancel() {
      this.showActionDialog = false;
    },

    onGridReady(params) {
      this.gridApi = params.api;
    },
    // データ更新時に該当行をハイライトするためのIDリストに追加する
    pushUpdatedId(id) {
      if (id && !this.updatedIdList.includes(id)) {
        this.updatedIdList.push(id);
        // ハイライトを反映するためにグリッドを再描画
        this.$nextTick(() => {
          if (this.gridApi) this.gridApi.redrawRows();
        });
        setTimeout(() => {
          const index = this.updatedIdList.indexOf(id);
          if (index > -1) {
            this.updatedIdList.splice(index, 1);
            // ハイライトを解除するためにグリッドを再描画
            if (this.gridApi) {
              this.gridApi.redrawRows();
            }
          }
        }, 3000);
      }
    },

    async fetchTableData() {
      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      try {
        const res = await enterpriseManningVesselStatusApi.fetch();
        this.tableData = res.data && res.data.list ? res.data.list : [];
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "船舶動静種別の取得に失敗しました",
          color: "danger",
          icon: "error",
        });
        this.tableData = [];
      } finally {
        this.$vs.loading.close();
      }
    },
    async onAdd() {
      const payload = {
        color_hex: this.dialogSelectedColor.replace("#", ""),
        default_minutes: this.dialogDefaultMinutes,
        name: this.dialogName,
        show_on_creating: this.dialogShowOnCreating,
        sort_order: this.tableData.length + 1,
      };

      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      try {
        const res = await enterpriseManningVesselStatusApi.create(payload);
        if (res.data && res.data.id) {
          this.pushUpdatedId(res.data.id);
        }
        this.$vs.notify({
          title: "船舶動静種別追加",
          text: "船舶動静種別を追加しました",
          color: "success",
          icon: "done",
        });
        this.showActionDialog = false;
        await this.fetchTableData();
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "船舶動静種別の追加に失敗しました",
          color: "danger",
          icon: "error",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    async onEdit() {
      const target = this.tableData.find(
        row => row.id === this.dialogTargetRowData.id
      );
      if (!target) {
        this.showActionDialog = false;
        return;
      }
      const payload = {
        color_hex: this.dialogSelectedColor.replace("#", ""),
        default_minutes: this.dialogDefaultMinutes,
        name: this.dialogName,
        show_on_creating: this.dialogShowOnCreating,
        sort_order: target.sort_order,
      };

      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      try {
        await enterpriseManningVesselStatusApi.update(target.id, payload);
        this.pushUpdatedId(target.id);
        this.$vs.notify({
          title: "",
          text: "船舶動静種別の変更を保存しました",
          color: "success",
          icon: "done",
        });
        this.showActionDialog = false;
        await this.fetchTableData();
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "船舶動静種別の更新に失敗しました",
          color: "danger",
          icon: "error",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
    onRowDragEnter() {
      // ドラッグ前の順序を保持
      if (this.orderIdsBeforeDrag === null) {
        this.orderIdsBeforeDrag = this.tableData.map(element => element.id);
      }
    },
    async onRowDragEnd(data) {
      // rowDragManaged=true のため、ドロップ時点ではグリッドの表示順が既に新順序になっているため、それをそのまま利用する
      const newOrderIds = [];
      data.api.forEachNode(node => {
        if (node.data && node.data.id != null) {
          newOrderIds.push(node.data.id);
        }
      });

      // 並び替えが行われていない場合は何もしない（比較にはドラッグ開始時に保存した順序を使用）
      if (this.orderIdsBeforeDrag === null) {
        return;
      }
      const originalOrderIds = this.orderIdsBeforeDrag;
      this.orderIdsBeforeDrag = null;

      if (
        originalOrderIds.length !== newOrderIds.length ||
        originalOrderIds.every((id, i) => id === newOrderIds[i])
      ) {
        return;
      }

      // 行データは id で検索するため tableData の配列順に依存しない（同一 id の行は1件のみ）
      const updatedData = newOrderIds
        .map((id, index) => {
          const item = this.tableData.find(t => t.id === id);
          if (!item) return null;
          return { ...item, sort_order: index + 1 };
        })
        .filter(Boolean);

      this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
      try {
        const updatePromises = updatedData.map(item =>
          enterpriseManningVesselStatusApi.update(item.id, {
            color_hex: item.color_hex,
            default_minutes: item.default_minutes,
            name: item.name,
            show_on_creating: item.show_on_creating,
            sort_order: item.sort_order,
          })
        );
        await Promise.all(updatePromises);
        this.tableData = updatedData;
        this.pushUpdatedId(data.node.data.id);
        this.$vs.notify({
          title: "",
          text: "船舶動静種別の変更を保存しました",
          color: "success",
          icon: "done",
        });
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "並び順の更新に失敗しました",
          color: "danger",
          icon: "error",
        });
        await this.fetchTableData();
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

/* ルート: ページ全体 */
#vessel-movement-type-list {
  .ag-grid-table {
    height: 100%;
    font-family: $aisea-font-family;
    font-size: 16px;
    font-weight: 400;

    .ag-header-cell-text {
      font-family: $aisea-font-family;
      font-size: 12px;
      font-weight: 600;
      white-space: pre-line;
    }

    .ag-header-cell,
    .ag-cell {
      padding-left: 10px;
      padding-right: 10px;
    }

    .ag-header-cell-center {
      .ag-header-cell-label {
        justify-content: center;
      }
    }
  }
}

/* 追加・編集ダイアログ */
.vessel-movement-type-list-add-dialog {
  .vs-dialog {
    max-width: 520px;

    .vs-dialog-text {
      max-height: 90vh;
      overflow: auto;
      padding: 0 !important;
    }

    .vs-dialog-header,
    .vs-dialog-cancel {
      display: none;
    }
  }

  .vessel-movement-type-list-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px 0 20px;
    background-color: #f8f8f8;
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;

    .vessel-movement-type-list-dialog-header-title {
      font-family: $aisea-font-family;
      font-size: 18px;
      font-weight: 600;
      color: $aisea-dark-gray;
      line-height: 20px;
    }

    .vessel-movement-type-list-dialog-header-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 6px;
      cursor: pointer;
      color: $aisea-dark-gray;
    }
  }

  .required-info-edit-form-container {
    display: flex;
    flex-direction: column;
    gap: 30px;
    padding: 20px 30px 30px;

    /* プレースホルダーを「カラーを選択してください」(.color-select-placeholder)と同じ見た目に統一 */
    input::placeholder,
    .vs-input--placeholder {
      color: $aisea-dark-gray;
      font-size: 14px;
    }
    .filter-input .menu-title.placeholder {
      color: $aisea-dark-gray;
      font-size: 14px;
    }

    .form-description {
      font-size: 16px;
    }

    .form-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .form-item-title {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0 6px;
        font-size: 12px;
        color: $aisea-dark-gray;
      }

      .input-area.color-select-wrapper {
        position: relative;
        width: 100%;

        .color-select-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          min-height: 38px;
          padding: 8px 12px;
          background: #fff;
          border: 1px solid $aisea-light-gray;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          user-select: none;

          &.placeholder .color-select-placeholder {
            color: $aisea-dark-gray;
          }

          &.open .color-select-chevron {
            transform: rotate(180deg);
          }
        }

        .color-select-value {
          display: flex;
          align-items: center;
        }

        .color-select-trigger-chip {
          min-width: 24px;
          max-width: 24px;
          height: 24px;
          border-radius: 50%;
        }

        .color-select-placeholder {
          color: #999;
        }

        .color-select-chevron {
          flex-shrink: 0;
          margin-left: 8px;
          color: $aisea-dark-gray;
          transition: transform 0.2s ease;
        }

        .color-select-menu {
          padding: 12px;
          background: #fff;
          border: 1px solid $aisea-light-gray;
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

          &.color-select-menu-fixed {
            margin-top: 4px;
          }

          .color-select-menu-label {
            font-size: 14px;
            color: $aisea-dark-gray;
            margin-bottom: 10px;
          }

          .color-select-swatch-grid {
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            gap: 10px;

            .color-select-swatch-wrap {
              width: 26px;
              height: 26px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 4px;
              cursor: pointer;
              border-radius: 50%;

              &.selected .color-select-swatch {
                border: 4px solid #fff;
                box-sizing: border-box;
              }

              .color-select-swatch {
                min-width: 26px;
                max-width: 26px;
                height: 26px;
                border-radius: 50%;

                &.focused {
                  outline: none;
                }
              }
            }
          }
        }
      }
    }

    .form-item-radio {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .form-item-radio-title {
        display: flex;
        gap: 4px;
        font-size: 12px;
        font-weight: 600;
        color: $aisea-dark-gray;
      }

      .form-item-radio-description {
        font-size: 12px;
      }
    }

    .required-mark {
      color: red;
    }

    .validation-label {
      color: red;
      margin: 4px 0;
    }

    .dialog-footer-button-container {
      display: flex;
      justify-content: flex-end;
      align-items: center;

      .dialog-footer-button {
        display: flex;
        gap: 10px;
      }
    }
  }
}

.color-dropdown-fade-enter-active,
.color-dropdown-fade-leave-active {
  transition: opacity 0.15s ease;
}
.color-dropdown-fade-enter,
.color-dropdown-fade-leave-to {
  opacity: 0;
}
</style>
